import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchExpenseByAny,
  fetchExpenseTotals,
  selectFetchedExpenses,
  selectExpenseLoading,
  selectExpenseTotals,
  deleteExpense,
} from "@/store/redux/slices/finance_management/expense_slice";
import { useLoader } from "@/hooks/LoaderProvider";
import Button from "@/components/common/Button";
import SearchFilterBar from "@/components/specific/Search_&_Filter";
import CustomPagination from "@/components/specific/Pagination";
import { useNavigate } from "react-router-dom";
import {
  DownOutlined,
  PlusOutlined,
  RightOutlined,
  Dropdown,
} from "@/config/antd";
import Add_Transaction_Dialog from "@/pages/finance_manager/components/Add_Transaction_Dialog";
import TransactionCard from "@/pages/finance_manager/components/Transaction_Card";
import FinancialHomeTable from "@/pages/finance_manager/components/Financial_Home_Table";
import Breadcrumb from "@/components/specific/Breadcrumb";
import TrashIcon from "@/assets/icons/web_Icons/trash_red.svg";
import TrendLine from "@/components/shared/Trend_Line";
import {
  formatCurrency,
  mapExpenseTotals,
} from "@/utils/finance_utils";
import EmptyState from "@/pages/finance_manager/components/Empty_State";
import { getOrganizationId } from "@/utils/token_functions";
import Success from "@/components/common/alerts/Success";
import Delete from "@/components/common/alerts/Delete";

const FinancialManagerHome = () => {
  const dispatch = useDispatch();
  const fetchedExpensesByOrg = useSelector(selectFetchedExpenses);
  const expenseTotals = useSelector(selectExpenseTotals);
  const isLoading = useSelector(selectExpenseLoading);
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const pageSize = 10;
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("all"); // 'all' | 'Today' | 'This Week' | 'This Month'
  const [filterDate, setFilterDate] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [deletedTransactionTitle, setDeletedTransactionTitle] = useState("");

  const getExpensesToUse = useCallback(() => {
    return fetchedExpensesByOrg || [];
  }, [fetchedExpensesByOrg]);

  const fetchOrgFinancialData = useCallback(async () => {
    const organizationID = getOrganizationId();
    if (!organizationID) return;

    await Promise.all([
      dispatch(
        fetchExpenseByAny({
          key: "organizationID",
          value: organizationID,
        })
      ).unwrap(),
      dispatch(fetchExpenseTotals(organizationID)).unwrap(),
    ]);
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      showLoader();
      try {
        await fetchOrgFinancialData();
      } catch (error) {
        console.error("Error fetching financial data:", error);
      } finally {
        hideLoader();
      }
    };

    fetchData();
  }, [fetchOrgFinancialData, showLoader, hideLoader]);

  const {
    totalOutflows,
    totalInflows,
    currentBalance,
    totalDonations: totalDonationsAmount,
    trendData: financialTrendData,
    fundsPercentChange,
    expensesPercentChange,
    donationsPercentChange,
  } = useMemo(() => mapExpenseTotals(expenseTotals), [expenseTotals]);

  const formatTrendPct = (value) => `${Math.abs(Number(value) || 0)}%`;
  const trendArrow = (value) => ((Number(value) || 0) >= 0 ? "↑" : "↓");

  // Transform transaction data for the cards (keep the original transformation)
  const transformedTransactions = getExpensesToUse().map((transaction) => ({
    id: transaction.id,
    _id: transaction._id,
    title: transaction?.name,
    user: transaction?.merchantInformation?.name,
    category: transaction?.itemCategoryID,
    amount:
      transaction?.amount?.toString() ||
      transaction?.grandTotal?.toString() ||
      "0",
    date: new Date().toLocaleDateString(),
    time: new Date().toLocaleTimeString(),
    status: "Pending",
  }));

  // Update filter change handler to set the date
  const handleFilterChange = (value) => {
    setFilterValue(value);

    if (value === "all") {
      setFilterDate(null);
      return;
    }

    if (typeof value === "string" && value.match(/\d{1,2} [A-Za-z]{3} \d{4}/)) {
      // Parse the date string (format: "dd MMM yyyy")
      const parts = value.split(" ");
      const day = parseInt(parts[0], 10);
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames.indexOf(parts[1]);
      const year = parseInt(parts[2], 10);

      if (month !== -1) {
        setFilterDate(new Date(year, month, day));
      }
    } else if (value === "Today") {
      setFilterDate(new Date());
    } else if (value === "This Week") {
      // Set to start of current week
      const now = new Date();
      const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - dayOfWeek);
      setFilterDate(startOfWeek);
    } else if (value === "This Month") {
      // Set to start of current month
      const now = new Date();
      setFilterDate(new Date(now.getFullYear(), now.getMonth(), 1));
    } else {
      setFilterDate(null); // Clear date filter
    }
  };

  // Update transformations to filter by date
  const getFilteredCardsData = useCallback(() => {
    let filtered = transformedTransactions;

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter((transaction) =>
        transaction.title?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case "approved":
        filtered = filtered.filter(
          (transaction) => transaction.status === "Approved"
        );
        break;
      case "pending":
        filtered = filtered.filter(
          (transaction) => transaction.status === "Pending"
        );
        break;
      case "rejected":
        filtered = filtered.filter(
          (transaction) => transaction.status === "Rejected"
        );
        break;
      default:
        break;
    }

    // Apply date filter
    if (filterDate) {
      filtered = filtered.filter((transaction) => {
        const transactionDate = new Date(transaction.date);

        if (filterValue === "Today") {
          return (
            transactionDate.getDate() === filterDate.getDate() &&
            transactionDate.getMonth() === filterDate.getMonth() &&
            transactionDate.getFullYear() === filterDate.getFullYear()
          );
        } else if (filterValue === "This Week") {
          // Filter for transactions in the same week
          const transactionTime = transactionDate.getTime();
          const weekEnd = new Date(filterDate);
          weekEnd.setDate(filterDate.getDate() + 7);
          return (
            transactionTime >= filterDate.getTime() &&
            transactionTime < weekEnd.getTime()
          );
        } else if (filterValue === "This Month") {
          // Filter for transactions in the same month
          return (
            transactionDate.getMonth() === filterDate.getMonth() &&
            transactionDate.getFullYear() === filterDate.getFullYear()
          );
        } else {
          // Specific date selected
          return (
            transactionDate.getDate() === filterDate.getDate() &&
            transactionDate.getMonth() === filterDate.getMonth() &&
            transactionDate.getFullYear() === filterDate.getFullYear()
          );
        }
      });
    }

    return filtered;
  }, [
    transformedTransactions,
    searchValue,
    activeFilter,
    filterDate,
    filterValue,
  ]);

  // Transform data for table (desktop view)
  const getFilteredTableData = useCallback(() => {
    let filtered = getExpensesToUse() || [];

    // Apply search filter
    if (searchValue.trim()) {
      filtered = filtered.filter((transaction) =>
        transaction?.name?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Apply status filter
    switch (activeFilter) {
      case "approved":
        filtered = filtered.filter(
          (transaction) => transaction.status === "Approved"
        );
        break;
      case "pending":
        filtered = filtered.filter(
          (transaction) => transaction.status === "Pending"
        );
        break;
      case "rejected":
        filtered = filtered.filter(
          (transaction) => transaction.status === "Rejected"
        );
        break;
      default:
        break;
    }

    // Apply date filter
    if (filterDate) {
      filtered = filtered.filter((transaction) => {
        // Try to get the transaction date from different properties
        const transactionDateString =
          transaction.date ||
          (transaction.createdAt
            ? new Date(transaction.createdAt).toLocaleDateString()
            : null);

        if (!transactionDateString) return true; // Skip filtering if no date is available

        const transactionDate = new Date(transactionDateString);

        if (filterValue === "Today") {
          return (
            transactionDate.getDate() === filterDate.getDate() &&
            transactionDate.getMonth() === filterDate.getMonth() &&
            transactionDate.getFullYear() === filterDate.getFullYear()
          );
        } else if (filterValue === "This Week") {
          // Filter for transactions in the same week
          const transactionTime = transactionDate.getTime();
          const weekEnd = new Date(filterDate);
          weekEnd.setDate(filterDate.getDate() + 7);
          return (
            transactionTime >= filterDate.getTime() &&
            transactionTime < weekEnd.getTime()
          );
        } else if (filterValue === "This Month") {
          // Filter for transactions in the same month
          return (
            transactionDate.getMonth() === filterDate.getMonth() &&
            transactionDate.getFullYear() === filterDate.getFullYear()
          );
        } else {
          // Specific date selected
          return (
            transactionDate.getDate() === filterDate.getDate() &&
            transactionDate.getMonth() === filterDate.getMonth() &&
            transactionDate.getFullYear() === filterDate.getFullYear()
          );
        }
      });
    }

    return filtered;
  }, [getExpensesToUse, searchValue, activeFilter, filterDate, filterValue]);

  const filteredTableData = getFilteredTableData();
  const filteredCardsData = getFilteredCardsData();

  const moreOptionsMenu = {
    items: [
      {
        key: "all",
        label: "All Transactions",
      },
      {
        key: "approved",
        label: "Approved",
      },
      {
        key: "pending",
        label: "Pending",
      },
      {
        key: "rejected",
        label: "Rejected",
      },
    ],
  };

  const handleOpenAddTransactionModal = () => {
    setShowAddTransactionModal(true);
  };

  // Update the search handler to use the search value
  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const showEmptyState = () => {
    // Show empty state if:
    // 1. No transactions at all
    // 2. Transactions exist but filtered results are empty (regardless of filter type)
    if (
      !isLoading &&
      (!getExpensesToUse() ||
        getExpensesToUse().length === 0 ||
        filteredTableData.length === 0)
    ) {
      return (
        <EmptyState
          onAddNew={handleOpenAddTransactionModal}
          message={
            searchValue
              ? "No transactions found matching your search"
              : activeFilter !== "all"
              ? `No ${activeFilter.toLowerCase()} transactions found`
              : filterValue !== "all"
              ? `No transactions found for ${filterValue}`
              : "No Transactions Found"
          }
        />
      );
    }
    return null;
  };

  const handleModify = useCallback(
    (transactionId) => {
      navigate(`/dashboard/financial/edit/${transactionId}`);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    async (transactionId, transactionTitle) => {
      showLoader();
      try {
        await dispatch(deleteExpense(transactionId)).unwrap();
        setDeletedTransactionTitle(transactionTitle);

        // Refetch data after deletion
        const organizationID = getOrganizationId();
        await dispatch(
          fetchExpenseByAny({
            key: "organizationID",
            value: organizationID,
          })
        ).unwrap();

        setIsSuccessVisible(true);
      } catch (error) {
        console.error("Delete failed:", error);
      } finally {
        hideLoader();
      }
    },
    [showLoader, hideLoader, dispatch]
  );

  const handleAddNewClick = () => {
    if (selectedTransactions.length > 0) {
      // Show delete confirmation dialog
      setIsDeleteModalVisible(true);
    } else {
      // Open add transaction modal
      setShowAddTransactionModal(true);
    }
  };

  // Function to delete multiple transactions
  const handleBulkDelete = async () => {
    if (selectedTransactions.length === 0) return;
    setIsDeleteModalVisible(false);
    showLoader();
    try {
      // Get the titles of the transactions being deleted for the success message
      const transactionsToDelete = getExpensesToUse().filter((transaction) =>
        selectedTransactions.includes(transaction._id)
      );
      const transactionCount = transactionsToDelete.length;

      // If only one transaction, use its name, otherwise use count
      if (transactionCount === 1) {
        setDeletedTransactionTitle(
          transactionsToDelete[0]?.name || "Transaction"
        );
      } else {
        setDeletedTransactionTitle(`${transactionCount} transactions`);
      }

      // Delete all selected transactions
      await Promise.all(
        selectedTransactions.map((transactionId) =>
          dispatch(deleteExpense(transactionId)).unwrap()
        )
      );

      // Refresh data and reset selection
      const organizationID = getOrganizationId();
      await dispatch(
        fetchExpenseByAny({
          key: "organizationID",
          value: organizationID,
        })
      ).unwrap();

      setSelectedTransactions([]);
      setIsSuccessVisible(true);
    } catch (error) {
      console.error("Bulk delete failed:", error);
    } finally {
      hideLoader();
    }
  };

  // breadcrumb items
  const items = [{ title: "Financial Manager", link: "dashboard/financial" }];

  // Define sort options based on your filter buttons
  const sortOptions = [
    { key: "all", label: "All Transactions" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "pending", label: "Pending" },
  ];

  const handleRefresh = useCallback(async () => {
    // 1. Reset pagination to first page
    setCurrentPage(1);

    // 2. Clear the search term
    setSearchValue("");

    // 3. Reset the active filter to 'all'
    setActiveFilter("all");

    // 4. Reset the display text for the filter button
    setFilterValue("all");
    setFilterDate(null);

    // 5. Fetch fresh data
    const organizationID = getOrganizationId();

    showLoader();
    try {
      await dispatch(
        fetchExpenseByAny({
          key: "organizationID",
          value: organizationID,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error refreshing transactions:", error);
    } finally {
      hideLoader();
    }
  }, [dispatch, showLoader, hideLoader]);

  return (
    <div className="w-full py-6 text-left sm:py-0">
      <Breadcrumb items={items} />
      <div className="items-center justify-between mb-6 lg:flex">
        <h1 className="heading-large text-darkGrey">Expense Management</h1>
        <Button
          data-testid="add-new-transaction-btn"
          text={
            selectedTransactions.length !== 0
              ? "DELETE SELECTED"
              : "ADD NEW TRANSACTION"
          }
          icon={
            selectedTransactions.length !== 0 ? (
              <img src={TrashIcon} />
            ) : (
              <PlusOutlined />
            )
          }
          color={selectedTransactions.length !== 0 ? "outline" : "primary"}
          className="border border-primary font-normal mt-4 text-[11px] !px-3 !py-2 h-8"
          iconPosition="left"
          customBgColor={selectedTransactions.length !== 0 ? "" : "white"}
          customTextColor={
            selectedTransactions.length !== 0 ? "red" : "primary"
          }
          onClick={handleAddNewClick}
        />

        {/* Add Transaction Modal */}
        <Add_Transaction_Dialog
          isVisible={showAddTransactionModal}
          onClose={() => setShowAddTransactionModal(false)}
        />
      </div>

      <div className="lg:border-2 border-[#eee] lg:p-4 rounded-xl">
        <div className="hidden mb-5 lg:block">
          <h3 className="font-bold text-[15px]">Transaction Overview</h3>
          <p className="text-xs">
            At a glance, all transaction range within the past 10 days. You can
            sort using the Calendar icon
          </p>
        </div>
        <div className="grid-cols-6 gap-4 lg:grid">
          {/* Top Box - Single row with flex */}
          <div className="p-2 lg:p-0 mb-4 lg:mb-0 bg-white rounded-xl border-2 lg:border-none border-[#eee]">
            <div className="flex items-start">
              {/* Current Balance */}
              <div
                onClick={() => navigate("/dashboard/financial/account")}
                className="w-full lg:border-2 border-[#eee] lg:p-2 rounded-xl">
                <h3 className="mb-2 text-small-xs text-darkGrey">
                  CURRENT BALANCE
                </h3>
                <div className="mb-4 font-bold heading-large text-darkGrey">
                  {formatCurrency(currentBalance)}
                </div>
                <button className="flex items-center px-4 py-2 text-black rounded-full bg-secondary text-small-xs">
                  VIEW MORE
                  <span className="ml-2">
                    <RightOutlined />
                  </span>
                </button>
              </div>

              {/* All Donations Mobile & Tablet */}
              <div
                onClick={() => navigate("/dashboard/financial/donations")}
                className="w-full lg:hidden">
                <h3 className="mb-2 text-small-xs text-darkGrey">
                  ALL DONATIONS
                </h3>
                <div className="mb-4 font-bold heading-large text-darkGrey">
                  {formatCurrency(totalDonationsAmount)}
                </div>
                <div className="flex items-center">
                  <div
                    className={`px-3 py-1 text-white rounded-full text-small-xs ${
                      donationsPercentChange >= 0 ? "bg-primary" : "bg-danger"
                    }`}>
                    <span className="preserve-text-white">
                      {trendArrow(donationsPercentChange)}
                    </span>
                    <span className="preserve-text-white">
                      {formatTrendPct(donationsPercentChange)}
                    </span>
                  </div>
                  <span className="ml-2 text-small-xs text-darkGrey">
                    from last month
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row - 2 columns grid */}
          <div className="grid grid-cols-2 gap-4 lg:col-span-4">
            {/* Total Inflows */}
            <div className="p-2 bg-white rounded-xl border-2 border-[#eee] lg:flex flex-row-reverse items-center lg:gap-2">
              <div className="h-16 bg-secondary rounded-lg mb-2">
                <TrendLine
                  data={financialTrendData.inflows}
                  color="#00BA07"
                  type="inflow"
                />
              </div>
              <div>
                <h3 className="mb-2 text-small-xs text-darkGrey">
                  TOTAL INFLOWS
                </h3>
                <div className="mb-4 font-bold heading-large text-darkGrey">
                  {formatCurrency(totalInflows)}
                </div>
                <div className="flex items-center">
                  <div
                    className={`flex items-center gap-1 px-3 py-1 text-white rounded-full text-small-xs ${
                      fundsPercentChange >= 0 ? "bg-green" : "bg-danger"
                    }`}>
                    <span className="preserve-text-white">
                      {trendArrow(fundsPercentChange)}
                    </span>
                    <span className="preserve-text-white">
                      {formatTrendPct(fundsPercentChange)}
                    </span>
                  </div>
                  <p className="ml-2 text-small-xs text-darkGrey">
                    from last month
                  </p>
                </div>
              </div>
            </div>

            {/* Total Outflows */}
            <div className="p-2 bg-white rounded-xl border-2 border-[#eee] lg:flex flex-row-reverse items-center lg:gap-2">
              <div className="h-16 bg-secondary rounded-lg mb-2">
                <TrendLine
                  data={financialTrendData.outflows}
                  color="#FF0000"
                  type="outflow"
                />
              </div>
              <div>
                <h3 className="mb-2 text-small-xs text-darkGrey">
                  TOTAL OUTFLOWS
                </h3>
                <div className="mb-4 font-bold heading-large text-darkGrey">
                  {formatCurrency(totalOutflows)}
                </div>
                <div className="flex items-center">
                  <div
                    className={`px-3 py-1 text-white rounded-full text-small-xs ${
                      expensesPercentChange >= 0 ? "bg-danger" : "bg-green"
                    }`}>
                    <span className="preserve-text-white">
                      {trendArrow(expensesPercentChange)}
                    </span>
                    <span className="preserve-text-white">
                      {formatTrendPct(expensesPercentChange)}
                    </span>
                  </div>
                  <span className="ml-2 text-small-xs text-darkGrey">
                    from last month
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* All Donations Desktop */}
          <div
            onClick={() => navigate("/dashboard/financial/donations")}
            className="w-full hidden lg:block border-2 border-[#eee] lg:p-2 rounded-xl">
            <h3 className="mb-2 text-small-xs text-darkGrey">ALL DONATIONS</h3>
            <div className="mb-4 font-bold heading-large text-darkGrey">
              {formatCurrency(totalDonationsAmount)}
            </div>
            <div className="flex items-center">
              <div
                className={`px-3 py-1 text-white rounded-full text-small-xs ${
                  donationsPercentChange >= 0 ? "bg-primary" : "bg-danger"
                }`}>
                <span className="preserve-text-white">
                  {trendArrow(donationsPercentChange)}
                </span>
                <span className="preserve-text-white">
                  {formatTrendPct(donationsPercentChange)}
                </span>
              </div>
              <span className="ml-2 text-small-xs text-darkGrey">
                from last month
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Section */}
      <div className="mt-8 lg:border-2 border-[#eee] rounded-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-5">
            <div>
              <h2 className="heading-medium text-darkGrey">
                {getExpensesToUse()?.length || 0} Total Transactions
              </h2>
              <p className="mb-4 text-small text-darkGrey">
                Total number of payment made
              </p>
            </div>
            <div className="items-center hidden h-8 gap-2 lg:flex">
              <Button
                text={`All Transactions (${getExpensesToUse()?.length || 0})`}
                color="primary"
                className="px-2 py-1 text-sm sm:px-3 sm:py-2 sm:text-small min-w-[80px] sm:min-w-[100px]"
                iconPosition="left"
                customBgColor={activeFilter === "all" ? "primary" : "white"}
                customTextColor={activeFilter === "all" ? "white" : "primary"}
                onClick={() => setActiveFilter("all")}
              />
              <Button
                text={`Approved (${
                  getExpensesToUse()?.filter((t) => t.status === "Approved")
                    .length || 0
                })`}
                className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
                customBgColor={
                  activeFilter === "approved" ? "primary" : "white"
                }
                customTextColor={
                  activeFilter === "approved" ? "white" : "primary"
                }
                onClick={() => setActiveFilter("approved")}
              />
              <Button
                text={`Rejected (${
                  getExpensesToUse()?.filter((t) => t.status === "Rejected")
                    .length || 0
                })`}
                className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
                customBgColor={
                  activeFilter === "rejected" ? "primary" : "white"
                }
                customTextColor={
                  activeFilter === "rejected" ? "white" : "primary"
                }
                onClick={() => setActiveFilter("rejected")}
              />
              <Button
                text={`Pending (${
                  getExpensesToUse()?.filter((t) => t.status === "Pending")
                    .length || 0
                })`}
                className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
                customBgColor={activeFilter === "pending" ? "primary" : "white"}
                customTextColor={
                  activeFilter === "pending" ? "white" : "primary"
                }
                onClick={() => setActiveFilter("pending")}
              />
            </div>
          </div>
          <Dropdown
            menu={{
              items: [
                { key: "all", label: "All Time" },
                { key: "Today", label: "Today" },
                { key: "This Week", label: "This Week" },
                { key: "This Month", label: "This Month" },
              ],
              onClick: ({ key }) => handleFilterChange(key),
              className: "custom-dropdown-menu",
            }}
            trigger={["click"]}>
          <Button
              text={
                filterValue === "all"
                  ? "FILTER BY"
                  : filterValue === "Today"
                  ? "Today"
                  : filterValue === "This Week"
                  ? "This Week"
                  : filterValue === "This Month"
                  ? "This Month"
                  : filterValue
              }
            icon={<DownOutlined />}
            iconPosition="right"
            className="border border-primary font-normal text-sm !px-3 !py-2 hidden h-8 lg:block" // Added size adjustments
            customBgColor="white"
            customTextColor="black"
          />
          </Dropdown>
        </div>

        <SearchFilterBar
          onSearch={handleSearch}
          onRefresh={handleRefresh}
          onFilterChange={(value) => {
            setActiveFilter(value);
          }}
          filterValue={filterValue}
          filterOptions={sortOptions}
          moreOptionsMenu={moreOptionsMenu}
          placeholder="Search transactions..."
          filterType="dropdown"
          sortOptions={sortOptions}
          searchValue={searchValue}
        />

        {isLoading ? (
          <div>Loading...</div>
        ) : (
          showEmptyState() || (
            <>
              <div className="hidden lg:block">
                <FinancialHomeTable
                  transactions={filteredTableData}
                  setSelectedTransactions={setSelectedTransactions}
                  isLoading={isLoading}
                  onDelete={handleDelete}
                  onModify={handleModify}
                />
              </div>

              <div className="lg:hidden">
                {/* Transaction Cards */}
                <div className="mb-6 mt-5">
                  {filteredCardsData.map((transaction) => (
                    <TransactionCard key={transaction.id} {...transaction} />
                  ))}
                </div>
              </div>

              {filteredTableData.length > 0 && (
                <div className="mt-3">
                  <CustomPagination
                    current={currentPage}
                    total={getExpensesToUse()?.length || 0}
                    pageSize={pageSize}
                    onChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )
        )}
      </div>

      {/* Success Modal */}
      <Success
        isVisible={isSuccessVisible}
        onClose={() => setIsSuccessVisible(false)}
        header="Transaction Deleted!"
        paragraph={`You have successfully deleted the transaction "${deletedTransactionTitle}" from your list.`}
        buttonText="Close"
        onButtonClick={() => setIsSuccessVisible(false)}
      />

      {/* Delete confirmation modal */}
      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={handleBulkDelete}
        header="Delete Selected Transactions?"
        paragraph={`Are you sure you want to delete ${selectedTransactions.length} selected transactions? This action cannot be undone.`}
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export default FinancialManagerHome;
